import { Layout, Menu, Typography } from "antd";
import React from "react";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  GlobalOutlined,
  NodeIndexOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store";
import { logout } from "../store/userSlice";
import homeIcon from "../assets/icons/home.svg";
import reportingIcon from "../assets/icons/reporting.svg";
import euroIcon from "../assets/icons/euro.svg";
import areaChart from "../assets/icons/area-chart.svg";
import personIcon from "../assets/icons/person.svg";
import appsIcon from "../assets/icons/apps.svg";
import booksIcon from "../assets/icons/books.svg";

const { Sider } = Layout;
const { Text } = Typography;

interface SidebarProps {
  collapsed: boolean;
  toggleCollapsed: () => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  toggleCollapsed,
  isMobile = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Récupérer l'utilisateur depuis le store Redux
  const { currentUser, isAuthenticated } = useAppSelector(
    (state) => state.user
  );

  const isAdmin = !!currentUser?.accesses?.includes("admin");
  const isSupplierUser = !!currentUser?.departmentFlags?.isSupplier;
  const resourcesTabEnabled = isAdmin || !isSupplierUser;

  const renderIcon = (src: string, alt: string) => (
    <img src={src} alt={alt} className="sidebar-svg-icon" />
  );

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === "/" || path.startsWith("/home")) return "home";
    if (path.startsWith("/rentabilite")) return "rentabilite";
    if (path.startsWith("/finance")) return "finance";
    if (path.startsWith("/fournisseurs")) return "fournisseurs";
    if (path.startsWith("/rh") || path.startsWith("/ressources-humaines"))
      return "rh";
    if (path.startsWith("/approvisionnement")) return "approvisionnement";
    if (path.startsWith("/ressources")) return "ressources";
    if (path.startsWith("/catalogue")) return "catalogue";
    if (path.startsWith("/distributeurs")) return "distributeurs";
    if (path.startsWith("/projets")) return "projets";
    if (path.startsWith("/harven")) return "harven";
    return "home";
  };

  React.useEffect(() => {
    if (!isAuthenticated && location.pathname !== "/login") {
      navigate("/login");
    }
  }, [isAuthenticated, navigate, location.pathname]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const menuItems = [
    ...(isAuthenticated && currentUser?.accesses?.includes("admin")
      ? [
          {
            key: "home",
            icon: renderIcon(homeIcon, "Accueil"),
            label: "Accueil",
            onClick: () => navigate("/"),
          },
          {
            key: "rentabilite",
            icon: renderIcon(areaChart, "Rentabilité"),
            label: "Rentabilité",
            onClick: () => navigate("/rentabilite"),
          },
          {
            key: "finance",
            icon: renderIcon(euroIcon, "Finance"),
            label: "Finance",
            onClick: () => navigate("/finance"),
          },
          {
            key: "fournisseurs",
            icon: renderIcon(reportingIcon, "Reporting"),
            label: "Reporting",
            onClick: () => navigate("/fournisseurs"),
          },
          {
            key: "catalogue",
            icon: renderIcon(appsIcon, "Catalogue"),
            label: "Catalogue",
            onClick: () => navigate("/catalogue"),
          },
          {
            key: "distributeurs",
            icon: <GlobalOutlined />,
            label: "Distributeurs",
            onClick: () => navigate("/distributeurs"),
          },
          {
            key: "approvisionnement",
            icon: <NodeIndexOutlined />,
            label: "Logistique",
            onClick: () => navigate("/approvisionnement"),
          },
          {
            key: "rh",
            icon: renderIcon(personIcon, "Ressources humaines"),
            label: "Ressources humaines",
            onClick: () => navigate("/rh"),
          },
          {
            key: "ressources",
            icon: renderIcon(booksIcon, "Ressources"),
            label: "Ressources",
            onClick: () => navigate("/ressources"),
          },
        ]
      : [
          ...(currentUser?.accesses?.includes("analyse")
            ? [{ key: "rentabilite", icon: renderIcon(reportingIcon, "Rentabilité"), label: "Rentabilité", onClick: () => navigate("/rentabilite") }]
            : []),
          ...(currentUser?.accesses?.includes("finance")
            ? [{ key: "finance", icon: renderIcon(euroIcon, "Finance"), label: "Finance", onClick: () => navigate("/finance") }]
            : []),
          ...(currentUser?.accesses?.includes("reporting")
            ? [{ key: "fournisseurs", icon: renderIcon(reportingIcon, "Fournisseurs"), label: "Fournisseurs", onClick: () => navigate("/fournisseurs") }]
            : []),
          ...(currentUser?.accesses?.includes("approvisionnement")
            ? [{ key: "approvisionnement", icon: renderIcon(appsIcon, "Logistique"), label: "Logistique", onClick: () => navigate("/approvisionnement") }]
            : []),
          ...(currentUser?.accesses?.includes("hr")
            ? [{ key: "rh", icon: renderIcon(personIcon, "Ressources humaines"), label: "Ressources humaines", onClick: () => navigate("/rh") }]
            : []),
          ...(resourcesTabEnabled
            ? [{ key: "ressources", icon: renderIcon(booksIcon, "Ressources"), label: "Ressources", onClick: () => navigate("/ressources") }]
            : []),
          ...(currentUser?.accesses?.includes("catalog")
            ? [{ key: "catalogue", icon: renderIcon(appsIcon, "Catalogue"), label: "Catalogue", onClick: () => navigate("/catalogue") }]
            : []),
          ...(currentUser?.accesses?.includes("retailers")
            ? [{ key: "distributeurs", icon: renderIcon(appsIcon, "Distributeurs"), label: "Distributeurs", onClick: () => navigate("/distributeurs") }]
            : []),
        ]),
  ];

  return (
    <Sider
      trigger={null}
      collapsible={!isMobile}
      collapsed={collapsed}
      width={isMobile ? "100%" : 280}
      collapsedWidth={isMobile ? 0 : 80}
      style={{
        overflow: "hidden",
        height: "100vh",
        position: isMobile ? "fixed" : "fixed",
        zIndex: 1000,
        left: 0,
        top: 0,
        bottom: 0,
        background: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        borderRight: "1px solid #f1f5f9",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          padding: collapsed ? "24px 0" : "32px 24px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          transition: "all 0.3s",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <div style={{
          width: 40,
          height: 40,
          background: "#0f172a",
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          flexShrink: 0
        }}>
          <img
            src="/monogramme-honnore-blanc.png"
            alt="H"
            style={{
              paddingTop: 4,
              width: 32,
              height: 32,
              objectFit: "cover",
            }}
          />
        </div>
        {!collapsed && (
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Text strong style={{ fontSize: 16, color: "#0f172a", lineHeight: 1.2 }}>Honnoré</Text>
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.05em", textTransform: "uppercase" }}>Intranet</Text>
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "0 12px" }}>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          style={{ border: "none" }}
          className="modern-sidebar-menu"
        />
      </div>

      <div style={{ padding: "16px 12px 24px" }}>
        {isAuthenticated && (
          <div style={{ 
            background: "#f8fafc", 
            borderRadius: 16, 
            padding: collapsed ? "8px" : "12px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 8,
            border: "1px solid #f1f5f9"
          }}>
            <div style={{ 
              width: 36, 
              height: 36, 
              borderRadius: 10, 
              background: "#e2e8f0", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              flexShrink: 0,
              fontSize: 14,
              fontWeight: 600,
              color: "#475569"
            }}>
              {currentUser?.firstName?.charAt(0)}{currentUser?.lastName?.charAt(0)}
            </div>
            {!collapsed && (
              <div style={{ minWidth: 0, flex: 1 }}>
                <Text strong style={{ fontSize: 13, display: "block", color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {currentUser?.firstName} {currentUser?.lastName}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                  {currentUser?.accesses?.includes("admin") ? "Administrateur" : "Utilisateur"}
                </Text>
              </div>
            )}
          </div>
        )}

        <Menu
          theme="light"
          mode="inline"
          style={{ border: "none" }}
          selectable={false}
          items={[
            {
              key: "logout",
              icon: <LogoutOutlined style={{ color: "#ef4444" }} />,
              label: <span style={{ color: "#ef4444", fontWeight: 500 }}>Déconnexion</span>,
              onClick: handleLogout,
              className: "logout-item"
            },
          ]}
        />
        
        {!isMobile && (
          <div 
            onClick={toggleCollapsed}
            style={{ 
              marginTop: 12,
              textAlign: "center", 
              cursor: "pointer", 
              color: "#94a3b8",
              fontSize: 18,
              transition: "color 0.2s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = "#475569"}
            onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
        )}
      </div>

      <style>{`
        .ant-menu-vertical {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0;
          flex-direction: column;
        }
        .modern-sidebar-menu.ant-menu {
          background: transparent;
        }
        .modern-sidebar-menu .ant-menu-item {
          height: 44px !important;
          line-height: 44px !important;
          border-radius: 10px !important;
          margin: 4px 0 !important;
          color: #64748b !important;
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
        }
        .modern-sidebar-menu .ant-menu-item:hover {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .modern-sidebar-menu .ant-menu-item-selected {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .modern-sidebar-menu .ant-menu-item-selected .anticon {
          color: #0f172a !important;
        }
        .modern-sidebar-menu .ant-menu-item-icon {
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
        }
        .modern-sidebar-menu .ant-menu-item-icon .anticon {
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
        }
        .sidebar-svg-icon {
          width: 18px;
          height: 18px;
          display: block;
          margin: 0 auto;
          filter: brightness(0) saturate(100%) invert(49%) sepia(8%) saturate(1089%) hue-rotate(176deg) brightness(92%) contrast(86%);
        }
        .sidebar-svg-icon--tall {
          width: 22px;
          height: 22px;
        }
        .sidebar-anticon-large {
          font-size: 22px !important;
        }
        .sidebar-anticon-large svg {
          width: 22px;
          height: 22px;
        }
        .modern-sidebar-menu .anticon {
          font-size: 18px;
        }
        .modern-sidebar-menu .anticon svg {
          width: 18px;
          height: 18px;
        }
        .modern-sidebar-menu .ant-menu-item:hover .sidebar-svg-icon {
          filter: brightness(0) saturate(100%) invert(9%) sepia(14%) saturate(1286%) hue-rotate(182deg) brightness(92%) contrast(96%);
        }
        .modern-sidebar-menu .ant-menu-item-selected .sidebar-svg-icon {
          filter: brightness(0) saturate(100%) invert(9%) sepia(14%) saturate(1286%) hue-rotate(182deg) brightness(92%) contrast(96%);
        }
        .logout-item {
          border-radius: 10px !important;
        }
        .logout-item:hover {
          background-color: #fef2f2 !important;
        }
        .ant-layout-sider-children {
          display: flex;
          flex-direction: column;
        }
      `}</style>
    </Sider>
  );
};

export default Sidebar;
